/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { UserLocation, CachedUserLocation } from './geolocation.types';

export const CACHE_KEY = 'r1s:userLocation';
export const CACHE_TTL_MS = 86_400_000; // 24 hours

/**
 * Reads cached location from sessionStorage.
 * Returns null if missing, malformed, or expired.
 */
export function getCachedLocation(): UserLocation | null {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return null;

        const parsed: CachedUserLocation = JSON.parse(raw);

        if (parsed.expiresAt < Date.now()) {
            sessionStorage.removeItem(CACHE_KEY);
            return null;
        }

        return parsed.location;
    } catch {
        sessionStorage.removeItem(CACHE_KEY);
        return null;
    }
}

/** Rounds a number to 4 decimal places for storage. */
function roundTo4Decimals(value: number): number {
    return Math.round(value * 10_000) / 10_000;
}

/** Writes a location to sessionStorage with a fresh TTL. Lat/lng are rounded to 4 decimal places. */
export function setCachedLocation(location: UserLocation): void {
    const roundedLocation: UserLocation = {
        ...location,
        latitude: roundTo4Decimals(location.latitude),
        longitude: roundTo4Decimals(location.longitude),
    };

    const entry: CachedUserLocation = {
        location: roundedLocation,
        expiresAt: Date.now() + CACHE_TTL_MS,
    };

    try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
    } catch {
        // sessionStorage may be full or unavailable; silently ignore
    }
}
